import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { FormattedNote } from '../../../hooks/queries/noteTypes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useUser } from '../../../hooks/queries/useAuth';
import { getUserDisplayName } from '../../../lib/user-utils';
import { getCachedUser, getDisplayName } from '../../../utils/userCache';
import { logger } from '../../../utils/logger';

interface NotePreviewProps {
  note: FormattedNote;
  onClick: () => void;
  isActive: boolean;
  isCurrentlyOpen?: boolean;
}

export function NotePreview({
  note,
  onClick,
  isActive,
  isCurrentlyOpen = false,
}: NotePreviewProps) {
  const { data: user } = useUser();
  const [userDisplayName, setUserDisplayName] = useState<string>('User');

  // Get user info for the note creator
  useEffect(() => {
    // Check if this is the current user
    const isCurrentUser = user?.id === note.userId;

    if (isCurrentUser) {
      setUserDisplayName(getUserDisplayName(user) || 'You');
      return;
    }

    // Otherwise, try to get from cache
    const cachedUser = getCachedUser(note.userId);
    if (cachedUser) {
      setUserDisplayName(getDisplayName(cachedUser));
      logger.store('Using cached user data for note preview', { userId: note.userId });
    } else {
      // Fallback to userInfo or default
      setUserDisplayName(note.userInfo?.name || 'User');
    }
  }, [note.userId, user, note.userInfo?.name]);

  // Date handling
  const date = new Date(note.createdAt);

  // Format time
  const formatTime = (date: Date) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    } catch (error) {
      logger.error('Error formatting date in NotePreview', {
        error,
        date,
        dateString: note.createdAt,
      });
      return '--:--'; // Fallback for invalid dates
    }
  };

  // Determine what to show in the preview
  const maxPreviewLength = 250;
  const isMarkdownImage = note.content.trim().startsWith('![');

  // Check if this note should have a summary (>100 chars) but doesn't yet
  const isSummaryLoading = !note.summary && note.content.trim().length > 100 && !isMarkdownImage;

  // Use the AI summary if available, otherwise fall back to truncated content
  const previewContent = isMarkdownImage
    ? note.content // Don't truncate images
    : isSummaryLoading
      ? 'Summary is being computed...' // Loading state
      : note.summary
        ? note.summary // Use AI summary if available
        : note.content.length > maxPreviewLength
          ? note.content.substring(0, maxPreviewLength) + '...'
          : note.content;

  return (
    <div className="group w-full h-full cursor-pointer relative" onClick={onClick}>
      <div
        className={`h-full rounded-lg border ${
          isActive
            ? 'border-blue-200 shadow'
            : 'border-transparent shadow-sm hover:shadow hover:border-gray-300'
        } transition-all overflow-hidden flex flex-col`}
      >
        {/* Note header with user info - minimal clean design */}
        <div
          className={`px-3 py-2 border-b flex items-center justify-between
            ${isActive ? 'border-blue-200 bg-blue-50' : 'border-transparent bg-gray-50'}`}
        >
          <div className="flex items-center">
            <p className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-700'}`}>
              {userDisplayName}
            </p>
          </div>
          <div className="text-[10px] text-gray-500">
            {new Date(note.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year:
                new Date(note.createdAt).getFullYear() !== new Date().getFullYear()
                  ? 'numeric'
                  : undefined,
            })}
            {', '}
            {formatTime(date)}
          </div>
        </div>

        {/* Note content */}
        <div className="p-3 flex-grow relative bg-white">
          {isCurrentlyOpen ? (
            <div className="h-full w-full flex flex-col justify-center items-center">
              <div className="p-3 bg-blue-50 rounded-lg text-center w-full">
                <div className="flex justify-center mb-2">
                  <svg
                    className="animate-pulse w-5 h-5 text-blue-500"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-medium text-blue-700">Note currently open</h4>
                <p className="text-xs text-blue-600 mt-1">
                  Content will be visible when you close the note
                </p>
              </div>
            </div>
          ) : isMarkdownImage ? (
            <div className="w-full max-h-24 flex items-center justify-center rounded overflow-hidden">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ ...props }) => {
                    const { src, alt } = props;
                    return (
                      <img
                        src={src || ''}
                        alt={alt || ''}
                        className="max-h-20 max-w-full object-contain"
                        loading="eager"
                        decoding="sync"
                      />
                    );
                  },
                  p: ({ children }) => {
                    return <div className="m-0 p-0">{children}</div>;
                  },
                }}
              >
                {note.content}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="pr-6">
              {/* Title from summary */}
              <div className="mb-2">
                <h4 className="font-semibold text-lg text-gray-900 line-clamp-1">
                  {note.summary || (
                    <span className="text-gray-600 font-normal italic">Untitled Note</span>
                  )}
                  {isSummaryLoading && (
                    <span className="inline-flex items-center ml-2">
                      <svg
                        className="animate-spin h-3 w-3 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </span>
                  )}
                </h4>

                {isSummaryLoading && (
                  <div className="text-xs text-blue-500 mt-0.5 flex items-center">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1"></span>
                    Generating summary...
                  </div>
                )}
              </div>

              {/* Note content preview */}
              <div className="text-sm text-gray-600 line-clamp-2">
                {(() => {
                  try {
                    // Get the content to display
                    const content =
                      previewContent !== note.summary ? previewContent : note.content.trim();

                    // Create a document fragment to parse and clean the content
                    const tempDiv = document.createElement('div');

                    // First convert markdown to plain text
                    const plainText = content
                      // Remove HTML tags
                      .replace(/<[^>]*>/g, '')
                      // Remove all markdown syntax comprehensively
                      .replace(/[#*_`~]/g, '')
                      .replace(/\[([^\]]+)\][^(]*\([^)]*\)/g, '$1')
                      .replace(/!\[[^\]]*\][^(]*\([^)]*\)/g, '')
                      .replace(/\n/g, ' ')
                      .replace(/\s+/g, ' ');

                    // Handle HTML entities by converting them via innerHTML
                    tempDiv.innerHTML = plainText;

                    // Get the text content (which will have entities decoded)
                    let cleanText = tempDiv.textContent || '';

                    // Final pass to catch any remaining entities
                    cleanText = cleanText
                      // Specifically target escaped characters that might remain
                      .replace(/&(?:amp|lt|gt|quot|apos|#x[0-9a-fA-F]+|#[0-9]+);/g, ' ')
                      // Replace any remaining & patterns that look like entities
                      .replace(/&[a-zA-Z0-9#]{1,10};/g, ' ')
                      // Clean up extra spaces created by replacements
                      .replace(/\s+/g, ' ')
                      .trim();

                    return cleanText;
                  } catch {
                    // Ultimate fallback: just remove everything suspicious
                    return (previewContent || note.content || '')
                      .replace(/<[^>]*>/g, '') // HTML tags
                      .replace(/&[^;]+;/g, ' ') // All entity-like patterns
                      .replace(/[#*_`~]/g, '') // Markdown chars
                      .replace(/\n/g, ' ') // Newlines
                      .replace(/\s+/g, ' ') // Extra spaces
                      .trim();
                  }
                })()}
              </div>

              {/* Arrow icon */}
              <div className="absolute top-10 right-2 text-gray-400 group-hover:text-blue-500 transition-colors">
                <ChevronRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
